import React, { useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, TextArea } from '@ygtechnologies/bygr-frontend-component-library';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { closeChangeRequest, getChangeRequest } from '@/services/change-requests';
import { Button, ButtonBar } from '@ygtechnologies/rubus-ui';
import Banner from '@/components/Banner';
import { ChangeRequest } from '@/types';

interface ChangeRequestCloseDialogProps {
    changeRequestId: number;
    show: boolean;
    onClose(): void;
    onSave(note: string): void;
}

const ChangeRequestCloseDialog: React.FC<ChangeRequestCloseDialogProps> = ({ show, onClose, onSave, changeRequestId }) => {
    const [note, setNote] = useState('');
    const [sending, setSending] = useState(false);

    const [changeRequest, setChangeRequest] = useState<ChangeRequest | null>(null);
    const handleOpen = async () => {
        setChangeRequest(await getChangeRequest(changeRequestId));
        reset();
    };

    const handleSaveClick = async () => {
        setSending(true);

        try {
            await closeChangeRequest(changeRequestId, note);
            triggerHandler(onSave, note);
            triggerHandler(onClose);
        } finally {
            setSending(false);
        }
    };

    const handleCancelClick = () => {
        triggerHandler(onClose);
    };

    const reset = () => {
        setNote('');
    };

    return (
        <Modal isOpen={show} toggle={handleCancelClick} onOpen={handleOpen} size="lg">
            <ModalHeader>
                <Trans>Close request</Trans>
            </ModalHeader>
            <ModalBody>
                <div>
                    <Banner type="warning">
                        <Trans>
                            Closing the change request means that no one can make any more changes. You will still be able to
                            view the request. The buyer will be see this message.
                        </Trans>
                        {changeRequest?.externalAgreementId && (
                            <>
                                <br />
                                <Trans>This will also cancel the corresponding external agreement.</Trans>
                            </>
                        )}
                    </Banner>
                </div>

                <div className="form-group">
                    <label>
                        <Trans>Message</Trans>
                    </label>
                    <TextArea
                        autoResize={true}
                        value={note}
                        name="note"
                        className="form-control"
                        placeholder={t`You can add a message to the buyer to explain why you are closing the change request`}
                        onChange={(e) => setNote(e.target.value)}
                        minRows={5}
                    />
                </div>
            </ModalBody>
            <ModalFooter>
                <ButtonBar margins="none">
                    <ButtonBar.Right>
                        <Button variant="tertiary" onClick={handleCancelClick} disabled={sending}>
                            <Trans>Cancel</Trans>
                        </Button>
                        <Button variant="primary" onClick={handleSaveClick} loading={sending}>
                            <Trans>Close request</Trans>
                        </Button>
                    </ButtonBar.Right>
                </ButtonBar>
            </ModalFooter>
        </Modal>
    );
};

export default ChangeRequestCloseDialog;